import React, { useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { LevelData } from 'Types';

export type LevelPreviewSheetState = 'closed' | 'peek' | 'expanded';

interface LevelPreviewSheetProps {
  state: LevelPreviewSheetState;
  level: LevelData | undefined;
  peekHeight: number;
  expandedHeight: number;
  safeBottom: number;
  onStateChange: (nextState: LevelPreviewSheetState) => void;
  onPlay: () => void;
}

const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

const LevelPreviewSheet = ({
  state,
  level,
  peekHeight,
  expandedHeight,
  safeBottom,
  onStateChange,
  onPlay,
}: LevelPreviewSheetProps) => {
  const dragStartY = useRef<number | null>(null);

  const currentHeight = useMemo(() => {
    if (state === 'closed') return 0;
    if (state === 'expanded') return expandedHeight;
    return peekHeight + safeBottom;
  }, [state, peekHeight, expandedHeight, safeBottom]);

  const contentBottomPadding =
    state === 'peek'
      ? Math.max(6, Math.round(safeBottom * 0.2) + 4)
      : Math.max(10, safeBottom + 10);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    dragStartY.current = event.clientY;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartY.current === null) return;
    const deltaY = event.clientY - dragStartY.current;
    dragStartY.current = null;

    if (deltaY <= -40) {
      onStateChange('expanded');
      return;
    }

    if (deltaY >= 40) {
      if (state === 'expanded') {
        onStateChange('peek');
      } else if (state === 'peek') {
        onStateChange('closed');
      }
    }
  };

  const title = level ? `Level ${level.level_number}: ${level.name}` : 'Level Preview';
  const description = level?.description || 'Select a level to preview it.';
  const previewSrc = level?.srcs ? `https://${level.srcs}` : '';

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1500,
        height: `${currentHeight}px`,
        transition: 'height 200ms ease-out',
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px',
        background: 'linear-gradient(180deg, #132336 0%, #071423 100%)',
        borderTop: '1px solid rgba(173, 177, 183, 0.25)',
        boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.35)',
        overflow: 'hidden',
        pointerEvents: state === 'closed' ? 'none' : 'auto',
        color: '#CED0D4',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: '10px',
          paddingBottom: '8px',
          cursor: 'ns-resize',
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
      >
        <Box
          sx={{
            width: '44px',
            height: '5px',
            borderRadius: '999px',
            backgroundColor: 'rgba(206, 208, 212, 0.45)',
          }}
        />
      </Box>

      <Box
        sx={{
          height: `calc(100% - 26px)`,
          paddingLeft: '16px',
          paddingRight: '16px',
          paddingBottom: `${contentBottomPadding}px`,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {state === 'peek' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 45%) minmax(0, 45%) minmax(0, 10%)',
              columnGap: '8px',
              alignItems: 'stretch',
            }}
          >
            <Box sx={{ minWidth: 0, textAlign: 'left' }}>
              <Box
                sx={{
                  color: '#CA7C62',
                  fontWeight: 700,
                  fontSize: '14px',
                  lineHeight: '18px',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  textAlign: 'left',
                }}
              >
                {title}
              </Box>
              <Box
                sx={{
                  marginTop: '4px',
                  fontSize: '12px',
                  lineHeight: '16px',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  textAlign: 'left',
                }}
              >
                {description}
              </Box>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: '72px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: '#E2E2E2',
                  border: '1px solid rgba(0, 0, 0, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {previewSrc ? (
                  <img
                    src={previewSrc}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <Box sx={{ fontSize: '12px', color: '#333' }}>Preview</Box>
                )}
              </Box>
            </Box>

            <Box
              onClick={onPlay}
              sx={{
                minWidth: 0,
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                cursor: 'pointer',
              }}
            >
              <Box
                sx={{
                  fontWeight: 700,
                  fontSize: '12px',
                  lineHeight: '13px',
                  letterSpacing: '0.4px',
                }}
              >
                PLAY
              </Box>
              <Box
                sx={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#CA7C62',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PlayArrowRoundedIcon sx={{ fontSize: '38px' }} />
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                color: '#CA7C62',
                fontWeight: 700,
                fontSize: '17px',
                lineHeight: '24px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Box>

            <Box
              sx={{
                marginTop: '12px',
                borderRadius: '10px',
                overflow: 'hidden',
                backgroundColor: '#E2E2E2',
                border: '1px solid rgba(0, 0, 0, 0.15)',
                minHeight: `${clamp(expandedHeight * 0.32, 120, 220)}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {previewSrc ? (
                <img src={previewSrc} style={{ maxWidth: '100%', maxHeight: '100%' }} />
              ) : (
                <Box sx={{ fontSize: '13px', color: '#333' }}>Preview unavailable</Box>
              )}
            </Box>

            <Box
              sx={{
                marginTop: '12px',
                fontSize: '14px',
                lineHeight: '20px',
                maxHeight: `${Math.max(90, expandedHeight * 0.22)}px`,
                overflowY: 'auto',
              }}
            >
              {description}
            </Box>

            <Box
              onClick={onPlay}
              sx={{
                marginTop: '14px',
                width: '100%',
                height: '46px',
                borderRadius: '999px',
                backgroundColor: '#CA7C62',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                fontWeight: 700,
                fontSize: '15px',
                cursor: 'pointer',
              }}
            >
              <PlayArrowRoundedIcon sx={{ fontSize: '22px' }} />
              Play
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default LevelPreviewSheet;
